import React from 'react'
import { flowRight, get } from 'lodash'
import { Redirect, withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { Trans, useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import { graphqlCurrentUserCached, isLoggedIn } from 'lib/utils/store/currentUser'
import queryString from 'query-string'
import { getFullPath } from 'lib/location'
import { setInfoBreadcrumb } from 'lib/sentry'
import { getLocale } from 'lib/utils/getLocale'

import ControlledModal from 'components/Modal/ControlledModal'
import VehicleSelection from './VehicleSelection'
import OnMount from 'components/Util/ReactLifecycle/OnMount'
import { DataLoading } from 'components/Util/Loading'
import Link from 'components/Link'
import WhatIsAlmaAccountInfo from 'components/UI/WhatIsAlmaAccountInfo/WhatIsAlmaAccountInfo'
import OrSpacer from 'components/UI/OrSpacer/OrSpacer'
import AuthLoginButton from 'components/Auth/AuthLoginButton/AuthLoginButton'

import getVehicleSelectionModal from 'graphql/view/getVehicleSelectionModal.gql'

const VehicleSelectionModal = ({ location, match, currentUser }) => {
    const { t, i18n } = useTranslation(['vehicles'])

    const modalQuery = useQuery(getVehicleSelectionModal, {
        skip: !isLoggedIn(currentUser),
        variables: {
            userId: get(currentUser, 'me.id', null),
        },
        fetchPolicy: fetchPolicy.NETWORK_ONLY,
    })

    const { data } = modalQuery

    const { action } = match.params

    const query = queryString.parse(location.search)

    const closeIfNotLoggedIn = query.unauthclose !== undefined

    if (closeIfNotLoggedIn && !isLoggedIn(currentUser)) {
        setInfoBreadcrumb('VehicleSelectionModal: User not logged in, close modal...')
        return <Redirect to={getFullPath(location, { hash: false })} />
    }

    return (
        <ControlledModal
            routedModal
            name="vehicle-selection-modal"
            context="vehicleSelectionModal"
            initialTitle={t('vehicles:vehicleSelection.modalTitleLoading')}
            requireUserInput={false}
        >
            {(modalProps) => (
                <DataLoading data={modalQuery}>
                    {() => (
                        isLoggedIn(currentUser) ? (
                            <>
                                <OnMount onMount={() => modalProps.setTitle(t('vehicles:vehicleSelection.modalTitle'))} />
                                <VehicleSelection
                                    vehicles={data.user ? data.user.vehicles : []}
                                    action={action}
                                />
                            </>
                        ) : (
                            <>
                                <OnMount onMount={() => modalProps.setTitle(t('vehicles:vehicleSelection.modalTitleNotLoggedIn'))} />
                                <p>
                                    <Trans i18nKey="vehicles:vehicleSelection.loginRequired.loginDescription">
                                        Jos sinulla on jo Alma-tunnus,
                                        <AuthLoginButton loginSuccessTarget={getFullPath(location)}>
                                            kirjaudu sisään
                                        </AuthLoginButton>.
                                    </Trans>
                                </p>
                                <OrSpacer t={t} />
                                <p>
                                    <Trans i18nKey="vehicles:vehicleSelection.loginRequired.newVehicleDescription">
                                        Mikäli sinulla ei vielä ole Nettix-tiliä, luomme sen sinulle kun
                                        <Link
                                            to={`${getLocale(i18n.language)}/lisaa-ajoneuvo?target=${encodeURIComponent(query.target)}`}
                                            trackName="addNewVehicle"
                                        >
                                            lisäät ajoneuvosi.
                                        </Link>
                                    </Trans>
                                </p>
                                <WhatIsAlmaAccountInfo />
                            </>
                        )
                    )}
                </DataLoading>
            )}
        </ControlledModal>
    )
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
    graphqlCurrentUserCached,
)(VehicleSelectionModal)
